import React, { useState, useEffect, useCallback } from 'react';
import { budget as budgetApi } from '../utils/api';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CATEGORIES = ['General','Housing','Food','Transport','Entertainment','Health','Subscriptions','Utilities','Insurance','Other'];
const CAT_ICONS  = { General:'📦',Housing:'🏠',Food:'🍔',Transport:'🚗',Entertainment:'🎬',
                     Health:'💊',Subscriptions:'📺',Utilities:'💡',Insurance:'🛡️',Other:'📌' };

function monthFirstDay(m,y) { return `${y}-${String(m).padStart(2,'0')}-01`; }

export default function BudgetPage({ activePlayerId }) {
  const toast = useToast();
  const now   = new Date();
  const [month,  setMonth]  = useState(now.getMonth()+1);
  const [year,   setYear]   = useState(now.getFullYear());
  const [items,  setItems]  = useState([]);
  const [totals, setTotals] = useState(null);
  const [showModal,setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form,    setForm] = useState({ name:'',cost:'',notes:'',is_recurring:false,
    recurrence_type:'months',recurrence_interval:1,category:'General',date:now.toISOString().split('T')[0] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!activePlayerId) return;
    setLoading(true);
    try {
      const [i,t] = await Promise.all([
        budgetApi.list(activePlayerId, month, year),
        budgetApi.total(activePlayerId, month, year),
      ]);
      setItems(i); setTotals(t);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activePlayerId, month, year]);

  useEffect(() => { load(); }, [load]);

  const defaultDate = () => {
    const cm = now.getMonth()+1, cy = now.getFullYear();
    return (month===cm && year===cy) ? now.toISOString().split('T')[0] : monthFirstDay(month,year);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name:'',cost:'',notes:'',is_recurring:false,
      recurrence_type:'months',recurrence_interval:1,category:'General',date:defaultDate() });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name:item.name,cost:item.cost,notes:item.notes||'',
      is_recurring:item.is_recurring,recurrence_type:item.recurrence_type||'months',
      recurrence_interval:item.recurrence_interval||1,category:item.category||'General',
      date:item.date?.split('T')[0]||'' });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast('Name is required','error'); return; }
    if (!form.cost||isNaN(Number(form.cost))) { toast('Valid cost is required','error'); return; }
    try {
      const payload = { ...form, cost:parseFloat(form.cost) };
      if (!payload.is_recurring) { payload.recurrence_type=null; payload.recurrence_interval=null; }
      if (editing) { await budgetApi.update(editing.id,payload); toast('Expense updated!','success'); }
      else         { await budgetApi.create({...payload,player_id:activePlayerId}); toast('Expense added!','success'); }
      setShowModal(false); load();
    } catch (e) { toast(e.message,'error'); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try { await budgetApi.delete(item.id); toast('Expense removed.','info'); load(); }
    catch (e) { toast(e.message,'error'); }
  };

  const changeMonth = (d) => {
    let m=month+d, y=year;
    if (m>12){m=1;y++;} if(m<1){m=12;y--;}
    setMonth(m); setYear(y);
  };

  // Group by category, compute per-category totals
  const grouped = items.reduce((acc, item) => {
    const cat = item.category||'General';
    if (!acc[cat]) acc[cat]=[];
    acc[cat].push(item);
    return acc;
  }, {});

  // Category breakdown for chart
  const catTotals = Object.entries(grouped)
    .map(([cat,its]) => ({ cat, total:its.reduce((s,i)=>s+Number(i.cost),0) }))
    .sort((a,b) => b.total-a.total);
  const maxCat = catTotals.length ? catTotals[0].total : 1;

  const isCurrent = month===now.getMonth()+1 && year===now.getFullYear();
  if (!activePlayerId) return <div className="empty-state"><p>Select a player</p></div>;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">💰 Budget</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Add Expense</button>
      </div>

      {/* Month nav */}
      <div className="month-nav">
        <button className="btn btn-sm" onClick={() => changeMonth(-1)}>◀</button>
        <div className="month-nav-label">
          <span>{MONTHS[month-1]}</span>
          <span style={{ color:'var(--text-muted)' }}>{year}</span>
          {isCurrent && <span style={{ fontFamily:'var(--font-mono)',fontSize:'0.5rem',color:'var(--accent-cyan)',letterSpacing:1 }}>NOW</span>}
        </div>
        <button className="btn btn-sm" onClick={() => changeMonth(1)}>▶</button>
      </div>

      {/* KPI totals */}
      {totals && (
        <div className="kpi-grid" style={{ gridTemplateColumns:'repeat(3,1fr)',marginBottom:20 }}>
          <div className="kpi-card kpi-gold">
            <div className="kpi-value">${Number(totals.total).toFixed(2)}</div>
            <div className="kpi-label">Total</div>
          </div>
          <div className="kpi-card kpi-cyan">
            <div className="kpi-value">${Number(totals.recurring).toFixed(2)}</div>
            <div className="kpi-label">Recurring</div>
          </div>
          <div className="kpi-card kpi-green">
            <div className="kpi-value">${Number(totals.one_time).toFixed(2)}</div>
            <div className="kpi-label">One-Time</div>
          </div>
        </div>
      )}

      {/* Category breakdown chart */}
      {catTotals.length > 1 && (
        <div className="card" style={{ marginBottom:20 }}>
          <div className="section-label" style={{ marginBottom:12 }}>Spending by Category</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {catTotals.map(({cat,total}) => (
              <div key={cat} className="budget-cat-bar-row">
                <span className="budget-cat-bar-label">{CAT_ICONS[cat]||'📦'} {cat}</span>
                <div className="budget-cat-bar-track">
                  <div className="budget-cat-bar-fill" style={{ width:`${(total/maxCat)*100}%` }} />
                </div>
                <span className="budget-cat-bar-value">${total.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="empty-state"><p>Loading budget...</p></div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="icon">💰</div>
          <p>No expenses for {MONTHS[month-1]} {year}</p>
          <br /><button className="btn btn-primary" onClick={openCreate}>+ Add First Expense</button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {Object.entries(grouped).map(([cat,catItems]) => (
            <div className="card" key={cat}>
              <div className="budget-cat-header">
                <span className="budget-cat-icon">{CAT_ICONS[cat]||'📦'}</span>
                <span className="budget-cat-name">{cat}</span>
                <span className="budget-cat-total">${catItems.reduce((s,i)=>s+Number(i.cost),0).toFixed(2)}</span>
              </div>
              <table className="data-table">
                <thead><tr><th>Name</th><th>Cost</th><th>Type</th><th>Date</th><th></th></tr></thead>
                <tbody>
                  {catItems.map(item => (
                    <tr key={item.id}>
                      <td>
                        <div style={{ fontWeight:600 }}>{item.name}</div>
                        {item.notes && <div style={{ fontSize:'0.7rem',color:'var(--text-muted)',marginTop:2 }}>{item.notes}</div>}
                      </td>
                      <td style={{ fontFamily:'var(--font-mono)',color:'var(--accent-gold)' }}>${Number(item.cost).toFixed(2)}</td>
                      <td>
                        {item.is_recurring
                          ? <span className="recur-badge">🔄 every {item.recurrence_interval>1?`${item.recurrence_interval} `:''}{item.recurrence_type}</span>
                          : <span style={{ fontSize:'0.7rem',color:'var(--text-muted)' }}>One-time</span>}
                      </td>
                      <td style={{ fontFamily:'var(--font-mono)',fontSize:'0.72rem',color:'var(--text-muted)' }}>
                        {item.date&&new Date(String(item.date).split('T')[0]+'T00:00:00').toLocaleDateString()}
                      </td>
                      <td>
                        <div className="btn-group">
                          <button className="btn btn-sm btn-icon" onClick={() => openEdit(item)}>✎</button>
                          <button className="btn btn-sm btn-icon btn-danger" onClick={() => remove(item)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal title={editing?'✎ Edit Expense':'+ Add Expense'} onClose={() => setShowModal(false)}
          footer={
            <><button className="btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>{editing?'Save':'Add'}</button></>
          }>
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name} autoFocus
              onChange={e => setForm({...form,name:e.target.value})} placeholder="Expense name..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Cost ($) *</label>
              <input className="form-input" type="number" step="0.01" min="0"
                value={form.cost} onChange={e => setForm({...form,cost:e.target.value})} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" value={form.category}
                onChange={e => setForm({...form,category:e.target.value})}>
                {CATEGORIES.map(c => <option key={c} value={c}>{CAT_ICONS[c]} {c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <input className="form-input" value={form.notes}
              onChange={e => setForm({...form,notes:e.target.value})} placeholder="Any extra details..." />
          </div>
          <div className="form-group">
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={form.date}
              onChange={e => setForm({...form,date:e.target.value})} />
            {!editing && <div style={{ fontFamily:'var(--font-mono)',fontSize:'0.6rem',color:'var(--text-muted)',marginTop:3 }}>
              Defaults to {isCurrent?'today':`1st of ${MONTHS[month-1]}`}
            </div>}
          </div>
          <div className="form-group">
            <label className="form-checkbox">
              <input type="checkbox" checked={form.is_recurring}
                onChange={e => setForm({...form,is_recurring:e.target.checked})} />
              Recurring expense / subscription
            </label>
          </div>
          {form.is_recurring && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Every</label>
                <input className="form-input" type="number" min="1" value={form.recurrence_interval}
                  onChange={e => setForm({...form,recurrence_interval:parseInt(e.target.value)||1})} />
              </div>
              <div className="form-group">
                <label className="form-label">Period</label>
                <select className="form-select" value={form.recurrence_type}
                  onChange={e => setForm({...form,recurrence_type:e.target.value})}>
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                </select>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}